import { Head, useForm } from "@inertiajs/react"
import { useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import PartnerSettingsLayout from "@/layouts/partner/settings-layout"
import PartnerLayout from "@/layouts/partner-layout"
import { partnerSettingsUserProfilePath } from "@/routes"

interface UserProfile {
  uid: string
  name: string
  email: string
  avatar_initials: string
  avatar_url: string | null
}

export default function UserProfileSettings({ user }: { user: UserProfile }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)

  const { data, setData, patch, processing } = useForm({
    partner_user: {
      name: user.name ?? "",
      avatar: null as File | null,
    },
  })

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.[0]) {
      setData("partner_user", {
        ...data.partner_user,
        avatar: e.target.files[0],
      })
      setPreview(URL.createObjectURL(e.target.files[0]))
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(partnerSettingsUserProfilePath(), { forceFormData: true })
  }

  const displayInitials = user.avatar_initials || "?"

  return (
    <PartnerLayout>
      <Head title="Settings — Profile" />
      <PartnerSettingsLayout>
        <form onSubmit={handleSubmit}>
          <div className="flex max-w-md flex-col gap-5">
            <h2 className="text-sm font-semibold">Your Profile</h2>

            <div className="flex items-center gap-4">
              {preview || user.avatar_url ? (
                <img
                  src={preview ?? user.avatar_url ?? ""}
                  alt={user.name}
                  className="size-12 rounded-full object-cover"
                />
              ) : (
                <div className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-full text-sm font-semibold">
                  {displayInitials}
                </div>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm"
                onClick={() => fileRef.current?.click()}
              >
                Change photo
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="full-name">Name</Label>
              <Input
                id="full-name"
                value={data.partner_user.name}
                onChange={(e) =>
                  setData("partner_user", {
                    ...data.partner_user,
                    name: e.target.value,
                  })
                }
              />
            </div>

            <div className="flex gap-2 pt-1">
              <Button size="sm" className="rounded-sm" loading={processing}>
                Save changes
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </PartnerSettingsLayout>
    </PartnerLayout>
  )
}
